import { useState, useEffect, useCallback, useRef } from 'react'
import { apiService } from '../../../services/api'
import { debug } from '../../../utils/debug'
import { useUser } from '../../../context/UserContext'

// Normalize operator codes to standard format
const normalizeOperatorCode = (code) => {
  const mapping = {
    'OPL': 'ORANGE',
    'ORANGE': 'ORANGE',
    'TMPL': 'TMOBILE',
    'TMOBILE': 'TMOBILE',
    'PLK': 'PLUS',
    'PLUS': 'PLUS',
    'P4': 'P4',
    'PLAY': 'P4'
  }
  return mapping[code] || code
}

// Get operator display name
const getOperatorName = (code) => {
  const nameMap = {
    'ORANGE': 'Orange',
    'TMOBILE': 'T-Mobile',
    'PLUS': 'Plus',
    'P4': 'Play'
  }
  return nameMap[code] || code
}

// Get operator color key
const getOperatorColor = (code) => {
  const colorMap = {
    'OPL': 'orange',
    'ORANGE': 'orange',
    'TMPL': 'tmobile',
    'TMOBILE': 'tmobile',
    'PLK': 'plus',
    'PLUS': 'plus',
    'P4': 'play',
    'PLAY': 'play'
  }
  return colorMap[code] || 'gray'
}

// Get operator status based on forecast vs goal
const getOperatorStatus = (operator, goalData, rulesData) => {
  const operatorCode = normalizeOperatorCode(operator.operator_code || operator.code)
  const operatorRules = rulesData?.operators?.[operatorCode] || {}
  const hasSalesData = operatorRules.has_sales_data

  if (hasSalesData === false) {
    return 'na'
  }

  const forecastPct = goalData?.forecast_vs_goal_percent

  if (forecastPct === undefined || forecastPct === null) {
    return 'medium'
  }

  if (forecastPct >= 100) {
    return 'good'
  } else if (forecastPct >= 80) {
    return 'medium'
  } else {
    return 'bad'
  }
}

// Calculate performance display
const getPerformanceDisplay = (operatorCode, goalData, trendData) => {
  const forecastPct = goalData?.forecast_vs_goal_percent
  const forecastPctTarget = goalData?.forecast_vs_goal_percent_target
  const goalAchievement = goalData?.achievement_percent
  const forecasted = goalData?.forecasted_sales
  const goalValue = goalData?.goal_value
  const goalValueTarget = goalData?.goal_value_target

  if (forecastPct !== undefined && forecasted !== undefined && goalValue) {
    const unit = (operatorCode === 'TMOBILE' || operatorCode === 'P4') ? 'akt.' : 'szt.'
    // Always show both MIN / TARGET — when no separate target, target = minimum (same %)
    const effectiveTargetPct = forecastPctTarget ?? forecastPct
    const effectiveTargetGoal = goalValueTarget ?? goalValue
    const valueStr = `${forecastPct.toFixed(1)}% / ${effectiveTargetPct.toFixed(1)}%`
    const detailStr = effectiveTargetGoal !== goalValue
      ? `(prognoza: ${Math.round(forecasted)} / cel: ${Math.round(goalValue)}~${Math.round(effectiveTargetGoal)} ${unit})`
      : `(prognoza: ${Math.round(forecasted)}/${Math.round(goalValue)} ${unit})`

    if (forecastPct >= 100) {
      return {
        icon: '🎯',
        text: 'Prognoza przekracza cel!',
        value: valueStr,
        detail: detailStr,
        type: 'success',
        forecastPct,
        forecastPctTarget: effectiveTargetPct
      }
    }
    if (forecastPct >= 80) {
      return {
        icon: '📈',
        text: 'Prognoza blisko celu',
        value: valueStr,
        detail: detailStr,
        type: 'info',
        forecastPct,
        forecastPctTarget: effectiveTargetPct
      }
    }
    if (forecastPct >= 60) {
      return {
        icon: '📊',
        text: 'Prognoza poniżej oczekiwań',
        value: valueStr,
        detail: detailStr,
        type: 'warning',
        forecastPct,
        forecastPctTarget: effectiveTargetPct
      }
    }
    return {
      icon: '⚠️',
      text: 'Prognoza znacznie poniżej celu',
      value: valueStr,
      detail: detailStr,
      type: 'error',
      forecastPct,
      forecastPctTarget: effectiveTargetPct
    }
  } else if (goalAchievement !== undefined && goalValue) {
    const actualMtd = goalData?.actual_sales_mtd || 0
    const unit = (operatorCode === 'TMOBILE' || operatorCode === 'P4') ? 'akt.' : 'szt.'
    return {
      icon: '📊',
      text: 'MTD',
      value: `${Math.round(actualMtd)}/${Math.round(goalValue)} ${unit}`,
      detail: `(${goalAchievement.toFixed(1)}%)`,
      type: 'info'
    }
  } else if (trendData && Object.keys(trendData).length > 0) {
    const trendDesc = trendData.trend_description || 'Brak danych'
    const last7 = trendData.last_7_working_days || 0
    const prev7 = trendData.prev_7_working_days || 0
    const unit = (operatorCode === 'TMOBILE' || operatorCode === 'P4') ? 'akt.' : 'szt.'

    if (trendDesc.includes('Wzrost')) {
      return {
        icon: '📈',
        text: trendDesc,
        value: `(${Math.round(last7)} vs ${Math.round(prev7)} ${unit})`,
        detail: '',
        type: 'success'
      }
    } else if (trendDesc.includes('Spadek')) {
      return {
        icon: '📉',
        text: trendDesc,
        value: `(${Math.round(last7)} vs ${Math.round(prev7)} ${unit})`,
        detail: '',
        type: 'error'
      }
    } else {
      const additionalText = ['PLK', 'P4'].includes(operatorCode) ? ' (brak celów miesięcznych)' : ''
      return {
        icon: '📊',
        text: trendDesc + additionalText,
        value: '',
        detail: '',
        type: 'info'
      }
    }
  }

  return {
    icon: '⚠️',
    text: 'Brak danych o celach i trendach',
    value: '',
    detail: '',
    type: 'warning'
  }
}

// Loading placeholder for performance
const getLoadingPerformance = () => ({
  icon: '⏳',
  text: 'Ładowanie...',
  value: '',
  detail: '',
  type: 'info'
})

// Create placeholder operator data (before operatorSummaries loads)
const createPlaceholderOperator = (code, rulesData) => ({
  name: getOperatorName(code),
  code: code,
  status: 'loading',
  nbv: null,
  visits: null,
  color: getOperatorColor(code),
  suggestions: [],
  operatorData: {
    operator_code: code,
    operator_name: getOperatorName(code),
    total_sales_7d: null,
    total_sales_30d: null,
    total_visits_7d: null,
    coverage_rate: null,
    coverage_rate_30d: null,
    nbv_special_attention_count: null,
    kams_visiting: true,
    total_salons: null
  },
  goalData: {},
  trendData: {},
  performance: getLoadingPerformance(),
  realSales7d: null,
  prognoza: null,
  coverage30d: null,
  isPlaceholder: true  // Flag to indicate this is placeholder data
})

export const useOverviewData = () => {
  const { hasOperatorAccess } = useUser()
  
  // Split loading states for progressive rendering
  const [loadingCore, setLoadingCore] = useState(true)      // Rules + order - very fast
  const [loadingSummaries, setLoadingSummaries] = useState(true)  // Operator summaries - slower
  const [loadingGoals, setLoadingGoals] = useState(true)    // Sales goals
  const [loadingTrends, setLoadingTrends] = useState(true)  // Sales trends
  const [error, setError] = useState(null)
  
  // Core data
  const [operatorStatus, setOperatorStatus] = useState([])
  const [rules, setRules] = useState(null)
  const [visualizationData, setVisualizationData] = useState(null)
  const [dataFreshness, setDataFreshness] = useState({})
  const [freshnessThresholds, setFreshnessThresholds] = useState({ green_hours: 24, yellow_hours: 48 })
  
  // Enhancement data (with per-operator loading states)
  const [stockExpositionData, setStockExpositionData] = useState({})
  const [salonAttributesData, setSalonAttributesData] = useState({})

  // Daily sales chart data
  const [dailySalesData, setDailySalesData] = useState(null)
  const [loadingDailySales, setLoadingDailySales] = useState(false)
  const [dailySalesDays, setDailySalesDays] = useState(14)
  const [productGroupNames, setProductGroupNames] = useState({})
  
  const [refreshingCache, setRefreshingCache] = useState(false)
  
  // Refs for tracking data without causing re-renders
  const coreDataRef = useRef({ rulesData: null, operatorOrder: [], operatorSummaries: null })
  const salesGoalsRef = useRef({})
  const trendsLookupRef = useRef({})

  // Transform operator data with all available data
  const transformOperators = useCallback((summaries, goals, trends, rulesData, operatorOrder) => {
    // If no summaries yet, create placeholders from operatorOrder
    if (!summaries || summaries.length === 0) {
      return operatorOrder
        .filter(code => hasOperatorAccess(code))
        .map(code => createPlaceholderOperator(code, rulesData))
    }

    const sortedSummaries = [...summaries].sort((a, b) => {
      const aCode = normalizeOperatorCode(a.operator_code)
      const bCode = normalizeOperatorCode(b.operator_code)
      const aIndex = operatorOrder.indexOf(aCode)
      const bIndex = operatorOrder.indexOf(bCode)

      if (aIndex === -1 && bIndex === -1) return 0
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })

    const accessibleSummaries = sortedSummaries.filter(op => {
      const operatorCode = normalizeOperatorCode(op.operator_code)
      return hasOperatorAccess(operatorCode)
    })

    return accessibleSummaries.map(op => {
      const operatorCode = normalizeOperatorCode(op.operator_code)
      const goalData = goals[operatorCode] || {}
      const trendData = trends[operatorCode] || {}
      const forecasted = goalData.forecasted_sales
      const goalValue = goalData.goal_value
      const realSales7d = trendData.last_7_working_days !== undefined
        ? Math.round(trendData.last_7_working_days)
        : (op.total_sales_7d || 0)

      // Use loading performance if no goals/trends data yet
      const hasGoalData = Object.keys(goalData).length > 0
      const hasTrendData = Object.keys(trendData).length > 0
      const performance = (hasGoalData || hasTrendData)
        ? getPerformanceDisplay(operatorCode, goalData, trendData)
        : getLoadingPerformance()

      return {
        name: op.operator_name,
        code: operatorCode,
        status: getOperatorStatus(op, goalData, rulesData),
        nbv: Math.round((op.coverage_rate || 0) * 100 * 10) / 10,
        visits: op.total_visits_7d || 0,
        color: getOperatorColor(operatorCode),
        suggestions: op.suggestions || [],
        operatorData: op,
        goalData: goalData,
        trendData: trendData,
        performance: performance,
        realSales7d: realSales7d,
        prognoza: forecasted ? `${Math.round(forecasted)}/${Math.round(goalValue || 0)}` : null,
        coverage30d: op.coverage_rate_30d || 0,
        isPlaceholder: false
      }
    })
  }, [hasOperatorAccess])

  // Update operator status with latest refs data
  const updateOperatorStatus = useCallback(() => {
    const { rulesData, operatorOrder, operatorSummaries } = coreDataRef.current
    if (!rulesData || operatorOrder.length === 0) return
    
    const transformed = transformOperators(
      operatorSummaries, 
      salesGoalsRef.current, 
      trendsLookupRef.current, 
      rulesData, 
      operatorOrder
    )
    setOperatorStatus(transformed)
  }, [transformOperators])

  // INSTANT FETCH: Rules + Order only (very fast, cached)
  useEffect(() => {
    const fetchCoreConfig = async () => {
      debug.component('Overview', 'Starting CORE config fetch (rules + order)')
      debug.time('Overview Core Config')

      setLoadingCore(true)
      setError(null)

      try {
        const [rulesResponse, operatorOrderResponse] = await Promise.all([
          apiService.getRules().catch((err) => {
            debug.warn('Rules fetch failed', err, 'Overview')
            return { data: { operators: {} } }
          }),
          apiService.getOperatorOrder().catch((err) => {
            debug.warn('Operator order fetch failed', err, 'Overview')
            return { data: { operators: [] } }
          })
        ])

        const rulesData = rulesResponse.data || { operators: {} }
        
        // Get operator order
        const operatorOrderData = operatorOrderResponse.data?.operators || []
        const operatorOrder = operatorOrderData.length > 0 
          ? operatorOrderData.map(op => op.operator_code)
          : ['TMOBILE', 'ORANGE', 'PLUS', 'P4']

        setRules(rulesData)
        
        // Store in ref
        coreDataRef.current = { ...coreDataRef.current, rulesData, operatorOrder }
        
        // Create placeholder operators immediately
        const placeholders = operatorOrder
          .filter(code => hasOperatorAccess(code))
          .map(code => createPlaceholderOperator(code, rulesData))
        setOperatorStatus(placeholders)
        
        debug.timeEnd('Overview Core Config')
      } catch (err) {
        debug.error('Error fetching core config', err, 'Overview')
        setError('Nie udało się załadować konfiguracji. Sprawdź połączenie z API.')
      } finally {
        setLoadingCore(false)
      }
    }

    fetchCoreConfig()
  }, [hasOperatorAccess])

  // ENHANCEMENT FETCH: Operator summaries (slower, cached)
  useEffect(() => {
    const fetchSummaries = async () => {
      setLoadingSummaries(true)
      try {
        debug.time('Overview Summaries Fetch')
        const response = await apiService.getOperatorSummaries()
        const operatorSummaries = response.data
        
        coreDataRef.current = { ...coreDataRef.current, operatorSummaries }
        updateOperatorStatus()
        
        debug.timeEnd('Overview Summaries Fetch')
      } catch (err) {
        debug.warn('Operator summaries fetch failed', err, 'Overview')
      } finally {
        setLoadingSummaries(false)
      }
    }

    fetchSummaries()
  }, [hasOperatorAccess, updateOperatorStatus])

  // ENHANCEMENT FETCH: Sales goals (runs once, updates via ref)
  useEffect(() => {
    const fetchGoals = async () => {
      setLoadingGoals(true)
      try {
        const response = await apiService.getSalesGoalsV2()
        const goals = response.data || {}
        salesGoalsRef.current = goals
        updateOperatorStatus()
      } catch (err) {
        debug.warn('Sales goals fetch failed', err, 'Overview')
      } finally {
        setLoadingGoals(false)
      }
    }

    fetchGoals()
  }, [hasOperatorAccess, updateOperatorStatus])

  // ENHANCEMENT FETCH: Sales trends (runs once, updates via ref)
  useEffect(() => {
    const fetchTrends = async () => {
      setLoadingTrends(true)
      try {
        const responses = await apiService.getAllSalesTrends()
        const lookup = {}
        if (Array.isArray(responses)) {
          const operators = ['ORANGE', 'TMOBILE', 'PLUS', 'P4']
          responses.forEach((response, index) => {
            if (response.data) {
              lookup[operators[index]] = response.data
            }
          })
        }
        trendsLookupRef.current = lookup
        updateOperatorStatus()
      } catch (err) {
        debug.warn('Sales trends fetch failed', err, 'Overview')
      } finally {
        setLoadingTrends(false)
      }
    }

    fetchTrends()
  }, [hasOperatorAccess, updateOperatorStatus])

  // ENHANCEMENT FETCH: Visualization data
  useEffect(() => {
    const fetchVisualization = async () => {
      try {
        const response = await apiService.getVisualizationData()
        setVisualizationData(response.data)
      } catch (err) {
        debug.warn('Visualization data fetch failed', err, 'Overview')
      }
    }

    fetchVisualization()
  }, [])

  // ENHANCEMENT FETCH: Data freshness
  useEffect(() => {
    const fetchFreshness = async () => {
      try {
        const response = await apiService.getDataFreshness()
        setDataFreshness(response.data?.operators || {})
        setFreshnessThresholds(response.data?.thresholds || { green_hours: 24, yellow_hours: 48 })
      } catch (err) {
        debug.warn('Data freshness fetch failed', err, 'Overview')
      }
    }

    fetchFreshness()
  }, [])

  // ENHANCEMENT FETCH: Stock/Exposition data - starts immediately
  useEffect(() => {
    const operators = ['ORANGE', 'TMOBILE', 'PLUS', 'P4'].filter(op => hasOperatorAccess(op))
    
    if (operators.length === 0) return

    // Initialize with loading state
    const initialResults = {}
    operators.forEach(op => {
      initialResults[op] = { loading: true, data: null, error: null }
    })
    setStockExpositionData(initialResults)

    // Fetch all in parallel
    const fetchAllStock = async () => {
      const fetchPromises = operators.map(async (operatorCode) => {
        try {
          const response = await apiService.getStockExpositionSummary(operatorCode)
          return { operatorCode, loading: false, data: response.data, error: null }
        } catch (err) {
          return { operatorCode, loading: false, data: null, error: err.message }
        }
      })

      const results = await Promise.all(fetchPromises)
      const resultMap = {}
      results.forEach(item => {
        resultMap[item.operatorCode] = {
          loading: item.loading,
          data: item.data,
          error: item.error
        }
      })
      setStockExpositionData(resultMap)
    }

    fetchAllStock()
  }, [hasOperatorAccess])

  // ENHANCEMENT FETCH: Salon attributes data - starts immediately
  useEffect(() => {
    const operators = ['ORANGE', 'TMOBILE', 'PLUS', 'P4'].filter(op => hasOperatorAccess(op))
    
    if (operators.length === 0) return

    // Initialize with loading state
    const initialResults = {}
    operators.forEach(op => {
      initialResults[op] = { loading: true, data: null, error: null }
    })
    setSalonAttributesData(initialResults)

    // Fetch all in parallel
    const fetchAllAttributes = async () => {
      const fetchPromises = operators.map(async (operatorCode) => {
        try {
          const response = await apiService.getSalonAttributesSummary(operatorCode)
          return { operatorCode, loading: false, data: response.data, error: null }
        } catch (err) {
          return { operatorCode, loading: false, data: null, error: err.message }
        }
      })

      const results = await Promise.all(fetchPromises)
      const resultMap = {}
      results.forEach(item => {
        resultMap[item.operatorCode] = {
          loading: item.loading,
          data: item.data,
          error: item.error
        }
      })
      setSalonAttributesData(resultMap)
    }

    fetchAllAttributes()
  }, [hasOperatorAccess])

  // ENHANCEMENT FETCH: Daily sales per operator + product group names for chart labels
  useEffect(() => {
    const fetchDailySales = async () => {
      setLoadingDailySales(true)
      try {
        const response = await apiService.getDailySales(dailySalesDays)
        setDailySalesData(response.data)

        // Build a name map from product groups for chart series labels.
        // e.g. { ORANGE_OPASKA: "Opaska SOS", ORANGE_LOCATOR: "Lokalizator Orange" }
        const nameMap = {}
        const operatorCodes = ['ORANGE', 'PLUS', 'TMOBILE', 'P4']
        await Promise.all(operatorCodes.map(async (op) => {
          try {
            const pgRes = await apiService.getProductGroups(op)
            const groups = pgRes.data?.groups || []
            for (const g of groups) {
              if (!g.is_default && !g.is_aggregate) {
                // Match the backend legacy_keys pattern: OPERATOR_GROUPCODE
                const seriesKey = `${op}_${g.code}`
                nameMap[seriesKey] = g.name
              }
            }
          } catch { /* operator may not have groups */ }
        }))
        setProductGroupNames(nameMap)
      } catch (err) {
        debug.warn('Daily sales fetch failed', err, 'Overview')
      } finally {
        setLoadingDailySales(false)
      }
    }

    fetchDailySales()
  }, [dailySalesDays])

  const loadMoreDailySales = useCallback((days) => {
    setDailySalesDays(days)
  }, [])

  // Refresh cache handler
  const handleRefreshCache = async () => {
    try {
      setRefreshingCache(true)
      await apiService.invalidateOverviewCache()
      window.location.reload()
    } catch (err) {
      debug.error('Failed to refresh cache', err, 'Overview')
      alert('Nie udało się odświeżyć cache. Spróbuj ponownie.')
    } finally {
      setRefreshingCache(false)
    }
  }

  // Helper functions
  const getCoverageLabel = (operator) => {
    const originalCode = operator.operatorData?.operator_code
    const operatorConfig = rules?.operators?.[originalCode]
    const isSpecialAttentionOnly = operatorConfig?.coverage_only_for_special_attention === true
    return isSpecialAttentionOnly ? "Pokrycie 14d (spec. uwaga)" : "Pokrycie 14d"
  }

  const getCoverage30dLabel = (operator) => {
    const originalCode = operator.operatorData?.operator_code
    const operatorConfig = rules?.operators?.[originalCode]
    const isSpecialAttentionOnly = operatorConfig?.coverage_only_for_special_attention === true
    return isSpecialAttentionOnly ? "Pokrycie 30d (spec. uwaga)" : "Pokrycie 30d"
  }

  const getCoverageValue = (operator) => {
    if (operator.isPlaceholder) return null
    const kamsVisiting = operator.operatorData.kams_visiting
    if (!kamsVisiting) return "N/A"
    return `${operator.nbv}%`
  }

  const getGoalColor = (operator) => {
    switch (operator.performance.icon) {
      case '🎯': return '#15803d'
      case '📈': return '#eab308'
      case '📊': return '#f59e0b'
      case '⚠️': return '#dc2626'
      default: return '#6b7280'
    }
  }

  const getAvgSales = (operator) => {
    if (operator.isPlaceholder) return null
    const salonsForAvg = operator.operatorData.salons_for_avg_calc || operator.operatorData.total_salons || 1
    const sales30d = operator.operatorData.total_sales_30d || 0
    return (sales30d / salonsForAvg).toFixed(1)
  }

  // Compute combined loading state for backward compatibility
  const loading = loadingCore

  return {
    // Loading states
    loading,           // Backward compatible - true only for core config
    loadingCore,       // Rules + order (very fast)
    loadingSummaries,  // Operator summaries (slower)
    loadingGoals,      // For partial updates
    loadingTrends,     // For partial updates
    error,
    
    // Data
    operatorStatus,
    rules,
    visualizationData,
    stockExpositionData,
    salonAttributesData,
    dataFreshness,
    freshnessThresholds,
    dailySalesData,
    loadingDailySales,
    dailySalesDays,
    productGroupNames,
    
    // Actions
    refreshingCache,
    handleRefreshCache,
    loadMoreDailySales,
    
    // Helpers
    getCoverageLabel,
    getCoverage30dLabel,
    getCoverageValue,
    getGoalColor,
    getAvgSales
  }
}

export default useOverviewData
